import { actionChannel, call, fork, put, select, take, takeEvery } from 'redux-saga/effects';
import { END, eventChannel } from 'redux-saga';

import { StartUpload, UpdateProgress, UploadError, UploadSuccess } from '@/store/upload/actions';
import { FileLoadSuccess } from '@/store/userfiles/actions';
import { ShowToast } from '@/store/toast/actions';
import { uploadFile as upload } from '@/services/ElasticApi';
import { UserFilesState } from '@/store/userfiles/types';
import { ApplicationState as State } from '@/store';
import { UploadActionTypes, UploadFilesAction } from '@/store/upload/types';
import { ShowFileUploadError, ShowFileUploadProgress, ShowFileUploadSuccess } from '@/store/mediaManager/actions';
import { Logger } from '@/services/errors/appLogger';

const getUserFiles = (state: State): UserFilesState => state.userFiles;

const createUploader = (file: File, expires?: number): any => {
    let emit;

    const chan = eventChannel((emitter): any => {
        emit = emitter;
        return (): void => {}; // eslint-disable-line
    });

    const uploadProgressCb = (params: { total: number; loaded: number }): void => {
        const percentage = Math.round((params.loaded * 100) / params.total);
        emit(percentage);
        if (percentage === 100) {
            emit(END);
        }
    };

    const uploadPromise = upload({ file, onUpload: uploadProgressCb, expiresAfterDays: expires || 0 });
    return [uploadPromise, chan, emit];
};

function* uploadProgressWatcher(chan): any {
    while (true) {
        // eslint-disable-line no-constant-condition
        const progress = yield take(chan);
        yield put(UpdateProgress(progress));
    }
}

function* uploadFiles(action): any {
    let emitter;
    yield put(StartUpload());
    const { file, expiresAfterDays } = action.payload;
    try {
        const [uploadPromise, chan, emit] = yield call(createUploader, file, expiresAfterDays);
        emitter = emit;
        yield fork(uploadProgressWatcher, chan);

        const resp = yield call(() => uploadPromise);
        const userFiles = yield select(getUserFiles);
        const { files = [] } = userFiles;

        const updatedFiles = [...files, resp];

        yield put(UploadSuccess(resp));

        yield put(FileLoadSuccess(updatedFiles));
    } catch (e) {
        Logger.error(e?.response?.data?.Error ?? e.message);
        emitter?.(END);
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e?.response?.data?.Error}`,
                duration: 6000,
            })
        );
        yield put(UploadError());
    }
}

function* uploadMediaFile(action: UploadFilesAction): any {
    const { file } = action.payload;
    let emitter;
    try {
        yield put(StartUpload());
        yield put(ShowFileUploadProgress(file.name));
        const [uploadPromise, chan, emit] = yield call(createUploader, file);
        emitter = emit;
        yield fork(uploadProgressWatcher, chan);
        const resp = yield call(() => uploadPromise);

        yield put(UploadSuccess());
        yield put(ShowFileUploadSuccess(file.name));

        const userFiles = yield select(getUserFiles);
        const { files } = userFiles;
        files.push(resp);
        yield put(FileLoadSuccess(files));
    } catch (e) {
        Logger.error(e?.response?.data?.Error ?? e.message);
        emitter?.(END);
        yield put(UploadError());
        yield put(ShowFileUploadError(file.name));
    }
}

function* watchMediaUpload() {
    const chan = yield actionChannel(UploadActionTypes.UPLOAD_MEDIA_FILES);
    while (true) {
        // eslint-disable-line no-constant-condition
        const action = yield take(chan);
        yield call(uploadMediaFile, action);
    }
}

function* watchUpload(): any {
    yield takeEvery(UploadActionTypes.UPLOAD_FILES, uploadFiles);
}

const uploadSagas = [fork(watchUpload), fork(watchMediaUpload)];

export default uploadSagas;
